import { useCallback } from 'react';
import { Base64 } from 'js-base64';
import {
  buildCodeContainerConfig,
  TEST_POLL_MAX_ATTEMPTS,
  useTestCustomProcessor,
} from 'new/components/flows/shared/customProcessor';
import { serializePackages, withByoRuntimeMode, type ByoCodeValue } from './model';

/** Input passed to {@link UseByoCodeTest.run} to execute a test run. */
export interface ByoCodeTestInput {
  /** Source path / selected file name to probe. */
  path: string;
  /** Source config used in the probe payload (from createFlowService). */
  sourceConfig: any;
  /**
   * Base64-encoded code to run. When omitted, it is derived from
   * `code.code` (custom mode). For predefined mode the shell should pass the
   * resolved container's base64 code explicitly.
   */
  codeBase64?: string;
  /** Whether to fetch the Nexla parser output stage before the post-processor. */
  fetchParserOutput?: boolean;
  /** Browser File object, required only for file-upload probe mode. */
  selectedFile?: File;
}

export interface UseByoCodeTest {
  /** Execute a test run for the given sample input. */
  run: (input: ByoCodeTestInput) => void;
  /** Stop polling for the current run. Does not abort the in-flight HTTP
   * request already on the wire, but guarantees no further poll requests. */
  cancel: () => void;
  /** True while either the parser or post-processor stage is loading. */
  isRunning: boolean;
  /** Nexla parser stage output (raw probe result). */
  parserOutput: any;
  /** Custom post-processor stage output (raw probe result). */
  postProcessorOutput: any;
  /**
   * Execution logs.
   *
   * TODO(byo): The underlying `useTestCustomProcessor` does not expose a
   * separate logs stream — `nexla_utils.publish_*_log` output is embedded in
   * the probe responses, not returned as a distinct field. Until the probe API
   * surfaces logs separately, this is `null`. Shells should read log lines from
   * `postProcessorOutput`/`parserOutput` response bodies if needed.
   */
  logs: string[] | null;
  /** Error message, if the last run produced a non-200 status. */
  error: string | null;
}

/**
 * Clean BYO-facing wrapper around {@link useTestCustomProcessor}.
 *
 * Reuses the existing two-phase polling logic verbatim; it only presents a
 * simpler, BYO-shaped surface (`run` / `isRunning` / `parserOutput` /
 * `postProcessorOutput` / `logs` / `error`).
 *
 * @param code        The BYO code value (used to derive base64 + packages).
 * @param credentialId Credential id to probe against.
 * @param useFileUploadProbe Use the isolated file-upload probe slice.
 * @param getProbeSampleCallback Optional adapter callback (DirectFlow/BYO).
 */
export function useByoCodeTest(
  code: ByoCodeValue,
  credentialId: number,
  useFileUploadProbe = false,
  getProbeSampleCallback?: (credentialId: number, stage: string, payload: any) => void,
): UseByoCodeTest {
  const {
    isLoadingParserOutput,
    isLoadingTestResult,
    parserOutput,
    testResult,
    error: transportError,
    test,
    cancel,
  } = useTestCustomProcessor(credentialId, useFileUploadProbe, getProbeSampleCallback, {
    maxPollAttempts: TEST_POLL_MAX_ATTEMPTS,
  });

  const run = useCallback(
    (input: ByoCodeTestInput) => {
      const codeBase64 = input.codeBase64 ?? Base64.encode(code.code ?? '');

      test(
        codeBase64,
        input.path,
        {
          // input.sourceConfig is live wizard form state, not the persisted
          // (buildByoSourcePayload) source_config, so it won't yet carry
          // custom_runtime.mode on a brand-new draft — force it here too so
          // File Preview/"Test on one file" probes classify as REPLICATION
          // (raw file) the same way the persisted save does.
          //
          // (`ui.byo_code_predefined_id` — a BYO-only UI marker the probe
          // endpoint rejects when null — is stripped once, centrally, in
          // useTestCustomProcessor.ts's test(), not here; every caller of
          // this hook funnels through it.)
          source_config: withByoRuntimeMode(input.sourceConfig ?? {}),
          code_container_config: buildCodeContainerConfig(
            serializePackages(code.packages ?? []),
            code.extraData,
          ),
        },
        input.fetchParserOutput ?? true,
        input.selectedFile,
      );
    },
    [test, code.code, code.packages, code.extraData],
  );

  const isRunning = isLoadingParserOutput || isLoadingTestResult;

  // Surface a friendly error when a finished stage came back non-200, or when
  // the underlying hook caught a transport/network failure mid-poll (e.g. a
  // connection drop) — that case never produces a status code at all, so it
  // must be checked first or it silently resolves to "no error".
  let error: string | null = null;
  const ppStatus = testResult?.statusCode;
  const parserStatus = parserOutput?.statusCode;
  if (transportError) {
    error = transportError;
  } else if (!isRunning) {
    if (ppStatus != null && ppStatus !== 200 && ppStatus !== 202) {
      error = `Post-processor run failed (status ${ppStatus}).`;
    } else if (parserStatus != null && parserStatus !== 200 && parserStatus !== 202) {
      error = `Parser run failed (status ${parserStatus}).`;
    }
  }

  return {
    run,
    cancel,
    isRunning,
    parserOutput,
    postProcessorOutput: testResult,
    logs: null, // See TODO in UseByoCodeTest.logs.
    error,
  };
}

export default useByoCodeTest;
